import type { ComponentProps } from 'react';

import { cn } from '../../lib/utils';
import { Accordion } from '../../primitives/accordion';
import { Badge } from '../../primitives/badge';
import { Icon } from '../../primitives/icon';
import { Text } from '../../primitives/text';

function VersionHistoryAccordion({
  className,
  ...props
}: ComponentProps<typeof Accordion>) {
  return (
    <Accordion
      data-slot={'version-history-accordion'}
      className={cn(className)}
      {...props}
    />
  );
}

interface VersionHistoryAccordionItemProps extends ComponentProps<
  typeof Accordion.Item
> {
  isActive?: boolean;
}

function VersionHistoryAccordionItem({
  className,
  isActive = false,
  children,
  ...props
}: VersionHistoryAccordionItemProps) {
  return (
    <Accordion.Item
      data-slot={'version-history-accordion-item'}
      data-active={isActive || undefined}
      className={cn(
        'group/version-history-item rounded-lg data-[open]:border-secondary',
        className
      )}
      {...props}
    >
      {children}
    </Accordion.Item>
  );
}

interface VersionHistoryAccordionTriggerProps extends Omit<
  ComponentProps<typeof Accordion.Trigger>,
  'children'
> {
  title: string;
  badges?: ComponentProps<typeof Badge>[];
  user?: string;
  timestamp?: string;
}

function VersionHistoryAccordionTrigger({
  title,
  badges,
  user,
  timestamp,
  className,
  ...props
}: VersionHistoryAccordionTriggerProps) {
  const showMetadata = Boolean(user || timestamp);

  return (
    <Accordion.Header
      className={cn(
        'border-b border-b-transparent group-data-[open]/version-history-item:border-b-neutral-border'
      )}
    >
      <Accordion.Trigger
        data-slot={'version-history-accordion-trigger'}
        variant={'card'}
        className={cn(
          'items-start gap-3 p-4 text-sm [&_[data-slot=accordion-trigger-icon]]:text-primary',
          'group-data-[open]/version-history-item:!bg-secondary-minimal',
          className
        )}
        {...props}
      >
        <div className={cn('flex min-w-0 flex-1 flex-col gap-2')}>
          <div className={cn('flex items-center gap-4')}>
            <span className={cn('text-lg font-bold text-primary')}>
              {title}
            </span>
            <div className={cn('flex items-center gap-1')}>
              {badges?.map((badge, index) => (
                <Badge key={index} {...badge} />
              ))}
            </div>
          </div>
          {showMetadata && (
            <div
              className={cn(
                'flex items-center gap-3 text-base font-normal text-muted-foreground'
              )}
            >
              {user && (
                <div className={cn('flex items-center gap-1')}>
                  <Icon
                    name={'user-01'}
                    className={cn('size-3 text-primary-hover')}
                  />
                  <Text
                    preset={'body-md'}
                    className={cn('text-primary-hover')}
                    render={<span />}
                  >
                    {user}
                  </Text>
                </div>
              )}
              {timestamp && (
                <div className={cn('flex items-center gap-1')}>
                  <Icon
                    name={'clock'}
                    className={cn('size-3 text-primary-hover')}
                  />
                  <Text
                    preset={'body-md'}
                    className={cn('text-primary-hover')}
                    render={<span />}
                  >
                    {timestamp}
                  </Text>
                </div>
              )}
            </div>
          )}
        </div>
      </Accordion.Trigger>
    </Accordion.Header>
  );
}

type VersionHistoryAccordionContentProps = ComponentProps<
  typeof Accordion.Content
>;

function VersionHistoryAccordionContent({
  className,
  children,
  ...props
}: VersionHistoryAccordionContentProps) {
  return (
    <Accordion.Content
      data-slot={'version-history-accordion-content'}
      className={cn('bg-secondary-minimal', className)}
      {...props}
    >
      {children}
    </Accordion.Content>
  );
}

VersionHistoryAccordion.Item = VersionHistoryAccordionItem;
VersionHistoryAccordion.Trigger = VersionHistoryAccordionTrigger;
VersionHistoryAccordion.Content = VersionHistoryAccordionContent;

export { VersionHistoryAccordion };
export type {
  VersionHistoryAccordionContentProps,
  VersionHistoryAccordionItemProps,
  VersionHistoryAccordionTriggerProps,
};
